import React, { useState, useEffect } from "react";
import { Navigate, useParams } from "react-router";

import TeamNameBox from "./TeamNameBox";
import TeamDescription from "./TeamDescription";
import TeamMember from "./TeamMember";
import MainGame from "../person-single/MainGame";
import BackToListButton from "../backtolistbutton/BackToListButton";
import { SonicDBEntry } from "../../common/classes/SonicDBEntry";
import { SNSData } from "../../common/classes/SNSInfo";
import { ImageSourceSetData } from "../../common/classes/ImageSourceSet";
import { SonicTermData } from "../../common/classes/SonicTerm";
import LoadingSpinner from "../../common/LoadingSpinner";
import ContentsTitle from "../../common/ContentsTitle";
import SNSInformation from "../../common/sns/SNSInformation";
import ContentReportModalContainer from "../../common/report/ContentReportModalContainer";
import YouTubeChannelContainer from "../media/youtube/YouTubeChannelContainer";

import styles from "./SingleTeam.module.scss";

import { useMetaData } from "../../utils/useMetaData";
import { getDefaultImage } from "../../utils/GeneralUtils";
import { PageTypes } from "../../utils/PageTypes";
import { useGetTeams, useGetTeamMembers } from "../../utils/useTeam";

const SingleTeamContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    items: { teams: team },
    isLoading,
  } = useGetTeams({ id });

  const { members } = useGetTeamMembers({ id });

  useMetaData(
    team?.[0]
      ? {
          title: team[0].name,
          description: team[0].description
            ? team[0].description
            : `SELeCTに掲載中のeスポーツチーム ${team[0].name} のページです。`,
          imgUrl: team[0].term?.img?.medium?.url ?? "",
        }
      : undefined
  );

  if (isLoading) {
    return <LoadingSpinner space={1} />;
  } else if (team !== undefined && team.length !== 0 && team[0].id == id) {
    return (
      <SingleTeam
        id={team[0].id}
        team_members={members}
        name={team[0].name}
        kana={team[0].kana}
        description={team[0].description}
        img={team[0].term?.img}
        color={team[0].term?.color}
        sns={team[0].sns}
        owner={team[0].owner}
        game={team[0].game}
      />
    );
  } else {
    return <Navigate to="/404" replace />;
  }
};

type Props = {
  id: string;
  team_members?: Array<SonicDBEntry>;
  name?: string;
  kana?: string;
  description?: string;
  img?: ImageSourceSetData;
  color?: string;
  sns?: SNSData;
  owner?: string;
  game?: Array<SonicTermData>;
};
export const SingleTeam: React.FC<Props> = ({
  id,
  team_members,
  name,
  description,
  img,
  color,
  sns,
  game,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);

  const onScroll = () => {
    const windowScroll = document.documentElement.scrollTop;

    let scrollTriggerPoint = 20;

    if (window.innerWidth <= 768) {
      scrollTriggerPoint = 400;
    }

    if (windowScroll >= scrollTriggerPoint) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
  }, []);

  const [isReportModalShown, setIsReportModalShown] = useState<boolean>(false);

  return (
    <>
      <main>
        <article className={styles["c-single-team"]}>
          <div id="singleTeam" className={styles["c-single-team__wrap"]}>
            <div className={styles["c-single-team__firstvisual"]}>
              <div
                className={
                  isScrolled
                    ? styles["c-single-team__picture-large-hide"]
                    : styles["c-single-team__picture-large"]
                }
              >
                <img
                  src={img?.large.url ? img?.large.url : getDefaultImage()}
                />
              </div>
            </div>
            <div className={styles["c-single-team__contents"]}>
              <TeamNameBox
                img_src={img?.large.url}
                name={name}
                isShown={isScrolled}
                team_color={color}
                openModal={() => setIsReportModalShown(true)}
              />
              <div className={styles["c-single-team__contents__inner"]}>
                {description ? (
                  <div className={styles["c-single-team__contents__block"]}>
                    <TeamDescription description={description} />
                  </div>
                ) : null}

                {team_members && team_members.length > 0 ? (
                  <div className={styles["c-single-team__contents__block"]}>
                    <ContentsTitle title={"チームメンバー"} />
                    <TeamMember
                      team_member={team_members}
                      logo_src={img?.thumbnail.url}
                      team_color={color}
                    />
                  </div>
                ) : null}

                {game && game.length > 0 ? (
                  <div className={styles["c-single-team__contents__block"]}>
                    <ContentsTitle title={"ゲーム"} />
                    <MainGame games={game} />
                  </div>
                ) : null}

                {sns?.youtube ? (
                  <div className={styles["c-single-team__contents__block"]}>
                    <ContentsTitle title={"メディア情報"} />
                    {sns.youtube ? (
                      <YouTubeChannelContainer
                        link={sns.youtube.toString()}
                        pageType="team"
                        contentId={id}
                      />
                    ) : null}
                  </div>
                ) : null}
                {sns?.facebook ||
                sns?.instagram ||
                sns?.twitch ||
                sns?.twitter ||
                sns?.youtube ? (
                  <div className={styles["c-single-team__contents__block"]}>
                    <ContentsTitle title={"SNS情報"} />
                    <SNSInformation
                      twitch={sns?.twitch?.toString()}
                      twitter={sns?.twitter?.toString()}
                      instagram={sns?.instagram?.toString()}
                      facebook={sns?.facebook?.toString()}
                      youtube={sns?.youtube?.toString()}
                      isTeam={true}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </article>
        <BackToListButton name="チーム" to="/team" max_width={960} />
      </main>

      <ContentReportModalContainer
        isModalShown={isReportModalShown}
        closeHandler={() => setIsReportModalShown(false)}
        pageType={PageTypes.Team}
        contentName={name}
      />
    </>
  );
};

export default SingleTeamContainer;
